import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Field, FieldArray, formValueSelector, change } from 'redux-form'
import { get, isEmpty, trim } from 'lodash'
import {
  FixedMessage,
  InputFieldReduxFormWrapper,
  SelectFieldReduxFormWrapper,
} from '@adc/ui-core'
import classNames from 'classnames/bind'
import { Validators } from '../../../../../util'
import {
  FORM_NAME,
  HIDDEN_FIELD_NAME,
  INTERESTED_PARTY_FORM_TYPE,
  OHIO_STATE_VALUE,
  OHIO_ENTITIES_ERROR_MSG,
} from '../constants'
import {
  required,
  inclusion,
  signerTitleOptions,
  entityTypeOptions,
  stateOptions,
  getCurrentLevel,
  rulesType,
  getErrorMsg,
  entityValidator,
  normalizeOwnership,
  allowNumbersAndDot,
  renderOwnershipLabel,
  getOwnershipValidators,
  parentEntityValidator,
} from '../utils'
import MultiSubEntity from '../multiSubEntity'
import styles from './styles.scss'
const cx = classNames.bind(styles)
const { validateCompanyName } = Validators
const elmId = 'entity_form'
const selector = formValueSelector(FORM_NAME)
const parentErrorMsg = getErrorMsg('Parent Entity')
const entityErrorMsg = getErrorMsg('Entity')
// This form relates to an entity form on design, it needs an individual underneath.
class InterestedPartyForm extends Component {
  static propTypes = {
    formKey: PropTypes.string,
    isRootParent: PropTypes.bool,
    fieldValues: PropTypes.object,
    onTitleChange: PropTypes.func,
    showTitlesOnly: PropTypes.bool,
    backgroundColor: PropTypes.object,
    isAutoCapitalize: PropTypes.bool,
    isOhioAsset: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    disableParentTitle: PropTypes.bool,
    contentOpenedContext: PropTypes.shape({
      contentOpened: PropTypes.object.isRequired,
      deleteKey: PropTypes.func.isRequired,
      updateContentOpened: PropTypes.func.isRequired,
    }).isRequired,
    ...rulesType,
  }

  static defaultProps = {
    isRootParent: false,
    showTitlesOnly: false,
    disableParentTitle: false,
  }

  get errorMsgHandler() {
    const { meta, isRootParent, fieldArraysIndex, parentEntityError } =
      this.props
    const error = get(meta, 'error', [])
    const submitFailed = get(meta, 'submitFailed', false)
    const errorContent = isRootParent
      ? parentEntityError
      : !isEmpty(error) && error[fieldArraysIndex]
    const showError = !isEmpty(errorContent) && submitFailed
    const globalErrorMsg = errorContent || []
    return { showError, globalErrorMsg }
  }

  render() {
    const {
      rules,
      formKey,
      dispatch,
      fieldValues,
      isRootParent,
      onTitleChange,
      showTitlesOnly,
      backgroundColor,
      isAutoCapitalize,
      isOhioAsset,
      disableParentTitle,
      contentOpenedContext,
    } = this.props
    const formKeyPrefix = formKey ? `${formKey}.` : ''

    const interestedParties = get(fieldValues, 'interestedParties', [])
    const entityType = get(fieldValues, 'entityType')
    const interestedPartyTitle = get(fieldValues, 'interestedPartyTitle')
    const interestedPartiesCount = interestedParties.length || 0
    const disableTitle = isRootParent && disableParentTitle

    const level = getCurrentLevel(formKey)
    let selfShowTitleOnly = showTitlesOnly
    let styleBackground

    if (isRootParent) {
      selfShowTitleOnly = false
    }

    if (backgroundColor && !selfShowTitleOnly) {
      styleBackground = { backgroundColor: backgroundColor.currentRGB }
    }

    const { globalErrorMsg, showError } = this.errorMsgHandler

    return (
      <div className={cx('root')} style={styleBackground}>
        {globalErrorMsg.map((err, i) => {
          return (
            <FixedMessage
              isVisible={showError}
              isCloseable={false}
              role='error'
              key={i}
              showIcon
              centered
            >
              {err}
            </FixedMessage>
          )
        })}
        <div
          className={cx(
            'content',
            selfShowTitleOnly ? 'closed' : 'opened',
            'ui',
            'form'
          )}
          style={styleBackground}
        >
          <div className={cx('field-wrapper')}>
            <Field
              name={`${formKeyPrefix}entityName`}
              type='input'
              label='Entity Name'
              elmId={`${formKeyPrefix}${elmId}_entity_name`}
              validate={[
                required({
                  msg: isRootParent ? parentErrorMsg.name : entityErrorMsg.name,
                }),
                validateCompanyName,
              ]}
              className={cx('field')}
              normalizeOnBlur={trim}
              placeholder='Enter the entity name'
              onChange={(event, value) => {
                if (onTitleChange) onTitleChange(event, value)
                dispatch(
                  change(FORM_NAME, `${formKeyPrefix}entityNameText`, value)
                )
              }}
              component={InputFieldReduxFormWrapper}
            />
            <Field
              name={`${formKeyPrefix}entityNameText`}
              type='hidden'
              elmId={`${elmId}_entity_name_text`}
              component={InputFieldReduxFormWrapper}
            />
            <p>
              The entity name must match the name listed on the Secretary of
              State website.
            </p>
          </div>
          <div className={cx('field-wrapper')}>
            <Field
              name={`${formKeyPrefix}state`}
              type='select'
              label='What state is your company registered in?'
              placeholder='Select'
              elmId={`${formKeyPrefix}${elmId}_state`}
              className={cx('field')}
              options={stateOptions(`${formKeyPrefix}${elmId}`)}
              validate={[
                required({
                  msg: isRootParent
                    ? parentErrorMsg.state
                    : entityErrorMsg.state,
                }),
                inclusion({
                  if: () => {
                    return Boolean(isOhioAsset)
                  },
                  in: [OHIO_STATE_VALUE],
                  msg: OHIO_ENTITIES_ERROR_MSG,
                }),
              ]}
              component={SelectFieldReduxFormWrapper}
            />
          </div>
          <div className={cx('field-wrapper')}>
            <Field
              name={`${formKeyPrefix}entityType`}
              type='select'
              label='Type'
              placeholder='Select'
              upward
              className={cx('field')}
              elmId={`${formKeyPrefix}${elmId}_entity_type`}
              validate={required({
                msg: isRootParent ? parentErrorMsg.type : entityErrorMsg.type,
              })}
              options={entityTypeOptions(`${formKeyPrefix}${elmId}`)}
              component={SelectFieldReduxFormWrapper}
            />
            <p>
              Entity documents are required if you are purchasing under your
              entity. Document requirements vary depending on entity type.
            </p>
          </div>
          <div className={cx('field-wrapper', 'title-and-ownership')}>
            <div className={cx('title-field')}>
              {!disableTitle && (
                <Field
                  name={`${formKeyPrefix}interestedPartyTitle`}
                  type='select'
                  label='Title'
                  upward
                  options={signerTitleOptions(
                    entityType,
                    `${formKeyPrefix}${elmId}`
                  )}
                  placeholder='Select'
                  disabled={!entityType}
                  className={cx('field')}
                  validate={required({
                    msg: isRootParent
                      ? parentErrorMsg.title
                      : entityErrorMsg.title,
                  })}
                  elmId={`${formKeyPrefix}${elmId}_title`}
                  component={SelectFieldReduxFormWrapper}
                />
              )}
              {interestedPartyTitle === 'OTHER' && (
                <Field
                  name={`${formKeyPrefix}interestedPartyOtherTitle`}
                  type='text'
                  validate={required({
                    msg: isRootParent
                      ? parentErrorMsg.title
                      : entityErrorMsg.title,
                  })}
                  elmId={`${formKeyPrefix}${elmId}_other_title`}
                  normalizeOnBlur={trim}
                  label='Other Title'
                  placeholder='Enter a title'
                  component={InputFieldReduxFormWrapper}
                />
              )}
            </div>
            {!isRootParent && (
              <div className={cx('ownership-field')}>
                <Field
                  name={`${formKeyPrefix}interestedPartyOwnership`}
                  type='text'
                  label={renderOwnershipLabel(cx('u-pl-1'))}
                  elmId={`${formKeyPrefix}${elmId}_ownership`}
                  inputLabel='%'
                  labelPosition='right'
                  className={cx('field')}
                  normalize={allowNumbersAndDot}
                  normalizeOnBlur={normalizeOwnership}
                  validate={getOwnershipValidators(INTERESTED_PARTY_FORM_TYPE)}
                  component={InputFieldReduxFormWrapper}
                />
              </div>
            )}
          </div>
          {isRootParent && (
            // Use this hidden field for validating the parent entity
            <Field
              name={HIDDEN_FIELD_NAME}
              type='hidden'
              validate={parentEntityValidator}
              hideWrapper
              elmId='hidden_validator'
              disabled
              component={InputFieldReduxFormWrapper}
            />
          )}
        </div>
        <FieldArray
          component={MultiSubEntity}
          name={`${formKeyPrefix}interestedParties`}
          formKeyPrefix={formKeyPrefix}
          interestedPartiesCount={interestedPartiesCount}
          level={level}
          rules={rules}
          interestedParties={interestedParties}
          entityType={entityType}
          showTitlesOnly={showTitlesOnly}
          isRootParent={isRootParent}
          validate={entityValidator}
          isAutoCapitalize={isAutoCapitalize}
          isOhioAsset={isOhioAsset}
          contentOpenedContext={contentOpenedContext}
        />
      </div>
    )
  }
}

export default connect((state, props) => {
  return {
    fieldValues: selector(state, props.formKey),
  }
})(InterestedPartyForm)
